import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';
import { CHORDS, MAJOR, PARTIDO, strokes, tonesOf, under, wrap } from './samba-parts';

const PROGS = [
  ['C', 'C7', 'F', 'G7'],
  ['C', 'A7', 'Dm', 'G7'],
  ['C', 'E7', 'Am', 'G7'],
  ['Dm', 'G7', 'C', 'A7'],
] as const;

const HOOK: Note[][] = [
  [[0, 'G5', 2], [2, 'E5', 1, 0.8], [3, 'G5', 3], [6, 'C6', 2], [8, 'B5', 1, 0.8], [9, 'C6', 3], [12, 'G5', 2], [14, 'E5', 2]],
  [[0, 'Bb5', 3], [3, 'G5', 3], [6, 'E5', 2], [8, 'G5', 2], [10, 'Bb5', 1, 0.8], [11, 'A5', 1, 0.8], [12, 'G5', 4]],
  [[0, 'A5', 2], [2, 'C6', 1, 0.8], [3, 'A5', 3], [6, 'F5', 2], [8, 'A5', 3], [11, 'G5', 1, 0.8], [12, 'F5', 2], [14, 'D5', 2]],
  [[0, 'B4', 2], [2, 'D5', 1, 0.8], [3, 'F5', 3], [6, 'G5', 2], [8, 'B5', 3], [11, 'A5', 1, 0.8], [12, 'G5', 2], [14, 'F5', 1, 0.8], [15, 'E5', 1, 0.85]],
];

const TUBAS: readonly (readonly [step: number, semis: number, len: number][])[] = [
  [[0, 0, 2.4], [3, 0, 0.9], [4, 7, 3.4], [8, 0, 2.4], [11, 0, 0.9], [12, 7, 3.4]],
  [[0, 0, 2.4], [3, 12, 0.9], [4, 7, 3.4], [8, 0, 2.4], [11, 7, 0.9], [12, 12, 1.8], [14, 7, 1.6]],
];

const KEYS = [0, 2, -2, -3, 3, 1, -4] as const;
const TAMBORINS = ['X.x.x..x.x..x.x.', 'X.x..x.x.x..X.x.'] as const;
const CARRETEIRO = 'X-xxX-xxX-xxX-xx';
const CAIXAS = ['x-oXx-oXx-oXx-oX', 'X-oxX-oxX-oxX-ox', 'x-oXx-oXx-oXxoXX'] as const;
const CHOCALHO = 'o-xXo-xXo-xXo-xX';
const AGOGOS: readonly (readonly [step: number, high: number][])[] = [
  [[0, 1], [2, 1], [3, 0], [6, 0], [8, 1], [10, 1], [11, 0], [14, 0]],
  [[0, 0], [2, 1], [4, 0], [7, 1], [9, 1], [12, 0], [14, 1]],
];
const TERCEIRAS = ['..x...X...x.X...', 'X..x..x.....X..x', '......xX..x...X.'] as const;
const REPIQUES = ['x..x..x.x..x..x.', 'x.x..x.xx.x..x.x'] as const;
const CALL = 'X.X.X.X.x.xxX.X.';
const CALL_END = 'X..X..X.X.x.XXXX';
const CONVENCOES = ['X..X..X...X.X...', 'X.X..X..X..X.X..', 'X..X..X.X.X.X...'] as const;
const BEATS = 'X...X...X...X...';
const ROLL = 'xxxxxxxxxxxxxxxx';
const SWING = 53;

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 3 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(BATERIA, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const tamP = ch.pick(TAMBORINS);
  const caixaP = ch.pick(CAIXAS);
  const agogoRow = ch.pick(AGOGOS);
  const terP = ch.pick(TERCEIRAS);
  const repP = ch.pick(REPIQUES);
  const convP = ch.pick(CONVENCOES);
  const tubaRow = ch.pick(TUBAS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: tonesOf(c) }]),
        MAJOR,
        [70, 86],
        79,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, SWING, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const groove1 = find(form, 'A');
  const rng = createRng(`bateria-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const seen = new Map<string, Ev>();
  const hit = (v: string, bar: number, step: number, vel: number) => {
    const had = seen.get(`${v}:${bar}:${step}`);
    if (had !== undefined) {
      had.vel = Math.max(had.vel, vel);
      return;
    }
    const ev: Ev = { v, t: g.at(bar, step), bar, step, vel };
    seen.set(`${v}:${bar}:${step}`, ev);
    events.push(ev);
  };
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });

  const apito = (bar: number, steps: readonly number[]) =>
    steps.forEach((step, k) => play('apito', bar, step, 'F7', k === steps.length - 1 ? s * 5 : s * 1.2, 0.9));
  const pattern = (v: string, bar: number, pat: string, scale: number, cut = 16) => {
    for (const h of hits(pat)) if (h.step < cut) hit(v, bar, h.step, human(h.vel * scale));
  };
  const surdos = (bar: number, scale: number, cut = 16) => {
    for (const step of [0, 8]) {
      if (step >= cut) continue;
      hit('surdo2', bar, step, human(0.9 * scale));
      hit('surdo1m', bar, step, human(0.45 * scale));
    }
    for (const step of [4, 12]) {
      if (step >= cut) continue;
      hit('surdo1', bar, step, human(scale));
      hit('surdo2m', bar, step, human(0.42 * scale));
    }
  };
  const cavaco = (bar: number, notes: readonly string[], scale: number, cut = 16) => {
    for (const k of strokes(PARTIDO, 0.32)) {
      if (k.step >= cut) continue;
      if (k.open) chord('cavaco', bar, k.step, k.up ? [...notes].reverse() : notes, s * 1.4, human(k.vel * scale), 0.007);
      else hit('chk', bar, k.step, human(k.vel * scale));
    }
  };
  const unison = (bar: number, step: number, vel: number) => {
    hit('surdo1', bar, step, vel);
    hit('surdo2', bar, step, vel * 0.9);
    hit('surdo3', bar, step, vel * 0.85);
    hit('caixa', bar, step, vel);
    hit('repique', bar, step, vel * 0.9);
    hit('tamborim', bar, step, vel * 0.85);
  };

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? CHORDS.C! : prog[i]!;
    const tones = tonesOf(c);
    const cav = c.cav.map(tr);
    const root = midi(wrap(tr(c.root), 33, 44));
    const drop = b.name === 'drop' || b.name === 'drop2';
    const band = drop || b.name === 'A' || (b.name === 'outro' && !end);

    if (end) {
      unison(b.bar, 0, 1);
      hit('prato', b.bar, 0, 1);
      hit('impact', b.bar, 0, 0.85);
      chord('harm', b.bar, 0, cav, g.barLength * 0.7, 0.9, 0.006);
      chord('cavaco', b.bar, 0, cav, g.barLength * 0.6, 0.95, 0.012);
      play('lead', b.bar, 0, tr('C6'), g.barLength * 0.7, 0.95);
      play('tuba', b.bar, 0, noteName(root), g.barLength * 0.6, 0.95);
      continue;
    }

    if (band) {
      const paradinha = drop && e >= 0.7 && b.index % 8 === 7 && !b.last;
      const cut = paradinha ? 8 : 16;
      const full = drop ? 1 : 0.85;
      surdos(b.bar, full, cut);
      if (drop || e >= 0.7) pattern('surdo3', b.bar, terP, 0.8 * full, cut);
      pattern('caixa', b.bar, caixaP, drop ? 0.95 : 0.8, cut);
      const carreteiro = b.name === 'drop2' && e >= 0.85 && b.index % 2 === 1;
      pattern('tamborim', b.bar, carreteiro ? CARRETEIRO : tamP, drop ? 1 : 0.85, cut);
      pattern('chocalho', b.bar, CHOCALHO, drop ? 1 : 0.8, cut);
      if (drop || e >= 0.8) {
        for (const [step, high] of agogoRow) if (step < cut) hit(high ? 'agogoH' : 'agogoL', b.bar, step, human(0.85));
      }
      if (drop) pattern('repique', b.bar, repP, 0.55, cut);
      cavaco(b.bar, cav, drop ? 0.9 : 0.75, cut);
      for (const [step, semis, len] of tubaRow) {
        if (step < cut) play('tuba', b.bar, step, noteName(root + semis), len * s * 0.92, human(0.9));
      }
      if (drop && e >= 0.8 && b.index % 2 === 0 && !paradinha) {
        events.push({ v: 'cuica', t: g.at(b.bar, 6), bar: b.bar, step: 6, vel: 0.7, dur: s * 1.6, note: 'A4', glide: { to: 'E5', at: s * 0.4, time: s * 1.1 } });
        events.push({ v: 'cuica', t: g.at(b.bar, 14), bar: b.bar, step: 14, vel: 0.65, dur: s * 1.6, note: 'E5', glide: { to: 'G4', at: s * 0.3, time: s * 1.2 } });
      }
      if (paradinha) {
        apito(b.bar, [0, 2, 4]);
        for (const h of hits('X..X..X.')) {
          unison(b.bar, 8 + h.step, human(0.95));
          chord('harm', b.bar, 8 + h.step, cav, s * 1.2, 0.9);
        }
      }
      if (drop && b.index % 8 === 0 && b.index > 0) hit('prato', b.bar, 0, 0.75);
      if (b.name === 'drop' && b.last && brk !== undefined) apito(b.bar, [8, 10, 12]);
      if (b.name === 'A' && b.last) pattern('repique', b.bar, CALL_END, 0.9);
    } else if (b.name === 'break') {
      if (b.index === 0) {
        for (const h of hits(convP)) {
          unison(b.bar, h.step, human(h.vel));
          chord('harm', b.bar, h.step, cav, s * 1.3, 0.95);
          chord('cavaco', b.bar, h.step, cav, s * 1.2, 0.9, 0.006);
        }
      } else if (b.last) {
        pattern('repique', b.bar, CALL_END, 0.95);
        for (const h of hits(ROLL)) hit('caixa', b.bar, h.step, 0.3 + (0.65 * h.step) / 16);
        for (const h of hits(BEATS)) {
          hit('surdo1', b.bar, h.step, 0.55 + (0.45 * h.step) / 12);
          hit('surdo2', b.bar, h.step, 0.5 + (0.4 * h.step) / 12);
        }
        hit('surdo3', b.bar, 14, 0.9);
        pattern('tamborim', b.bar, tamP, 0.8);
      } else {
        pattern('tamborim', b.bar, tamP, 0.7);
        if (e >= 0.7) for (const [step, high] of agogoRow) hit(high ? 'agogoH' : 'agogoL', b.bar, step, human(0.6));
        if (b.index >= 2) {
          pattern('chocalho', b.bar, CHOCALHO, 0.7);
          for (const step of [4, 12]) hit('surdo1', b.bar, step, human(0.55));
          cavaco(b.bar, cav, 0.55);
        }
        if (e >= 0.8 && b.index === 1) {
          events.push({ v: 'cuica', t: g.at(b.bar, 4), bar: b.bar, step: 4, vel: 0.75, dur: s * 3, note: 'G4', glide: { to: 'D5', at: s * 0.5, time: s * 2 } });
          events.push({ v: 'cuica', t: g.at(b.bar, 12), bar: b.bar, step: 12, vel: 0.7, dur: s * 3, note: 'D5', glide: { to: 'F4', at: s * 0.5, time: s * 2.2 } });
        }
      }
    } else if (b.name === 'intro') {
      if (b.index === 0) {
        apito(b.bar, [0, 2, 4]);
        pattern('chocalho', b.bar, '........o-xXo-xX', 0.6);
        if (!b.last) cavaco(b.bar, cav, 0.5);
      }
      if (b.index > 0 && !b.last) {
        pattern('repique', b.bar, CALL, 0.75);
        pattern('chocalho', b.bar, CHOCALHO, 0.6);
        cavaco(b.bar, cav, 0.55);
        if (b.index >= b.bars / 2) {
          surdos(b.bar, 0.5);
          pattern('tamborim', b.bar, tamP, 0.6);
          pattern('caixa', b.bar, caixaP, 0.5);
        }
      }
      if (b.last) {
        pattern('repique', b.bar, CALL_END, 0.95);
        for (const h of hits(ROLL)) hit('caixa', b.bar, h.step, 0.25 + (0.6 * h.step) / 16);
        for (const h of hits(BEATS)) {
          hit('surdo1', b.bar, h.step, 0.45 + (0.4 * h.step) / 12);
          hit('surdo2', b.bar, h.step, 0.4 + (0.4 * h.step) / 12);
        }
        cavaco(b.bar, cav, 0.6);
      }
    }

    const row = hook[i]!;
    const lead = (vel: number, harm: number, octave: boolean) => {
      for (const [step, note, len, v] of row) {
        const n = tr(note);
        play('lead', b.bar, step, n, len * s * 0.9, human((v ?? 0.95) * vel));
        if (harm > 0) play('harm', b.bar, step, tr(under(note, tones)), len * s * 0.88, (v ?? 0.9) * harm);
        if (octave) play('harm', b.bar, step, transpose(n, -12), len * s * 0.86, (v ?? 0.9) * 0.5);
      }
    };
    if (b.name === 'A') lead(0.8, 0, false);
    if (b.name === 'drop') lead(1, 0.6, e >= 0.85);
    if (b.name === 'drop2') lead(1, 0.65, e >= 0.6);
    if (b.name === 'outro' && !end && b.index < 2) lead(0.75, 0.4, false);
  }

  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('prato', bar, 0, 1);
    hit('impact', bar, 0, 1);
  }
  if (groove1 !== undefined) hit('prato', groove1, 0, 0.7);

  return {
    id: trackId(BATERIA, seed, p),
    template: BATERIA.id,
    seed,
    params: p,
    title: ch.orig ? BATERIA.ref.title : titleOf(seed),
    genre: BATERIA.genre,
    bpm: p.bpm,
    swing: SWING,
    lufs: -10.5,
    key,
    form,
    mix: { reverbDecay: 1.4, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.18, drive: 1.4, softness: 1.3, pumpDepth: 0.2, pumpRelease: 0.2 },
    events,
    autos: [],
    accents: { surdo1: 'kick', surdo2: 'kick', tamborim: 'stab', lead: 'melody', impact: 'drop' },
  };
}

export const BATERIA: Template = {
  id: 'bateria',
  genre: 'samba de bloco / bateria',
  bpm: [126, 140],
  ref: { seed: 'concentracao', title: 'Concentração', params: { bpm: 130, bars: 28, energy: 0.55 } },
  score,
};
